import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import PHQForm from "./PHQForm";

// ⬅️ import thunk
import { useDispatch } from "react-redux";
import { createPhqAssessment } from "../../../store/slices/phqAssessmentsSlice";
import { getApiErrorMessage } from "../../../utils/apiError";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddPHQ = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        clientName: "",
        date: new Date().toISOString().split("T")[0],
        q1: "", q2: "", q3: "", q4: "", q5: "", q6: "", q7: "", q8: "", q9: "",
        difficulty: ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    setFormData((prev) => ({
                        ...prev,
                        clientName: patient.first_name + " " + patient.last_name
                    }));
                })
                .catch((err) => {
                    console.error("Failed to fetch patient", err);
                });
        }
    }, [patientId, dispatch]);

    // 🔥 Converts FE data → BE snake_case structured payload
    const buildPayload = () => {
        const scoreLabels = [
            "Not at all",
            "Several days",
            "More than half the days",
            "Nearly every day"
        ];

        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            difficulty: formData.difficulty,
            client_name: formData.clientName,
            status: "active",
        };

        // Map each question
        for (let i = 1; i <= 9; i++) {
            const qScore = parseInt(formData[`q${i}`] || 0, 10);
            const qValue = scoreLabels[qScore] || "";

            // Create dynamic snake_case field names
            payload[`q${i}_${getFieldSuffix(i)}_score`] = qScore;
            payload[`q${i}_${getFieldSuffix(i)}_value`] = qValue;
        }

        return payload;
    };

    // 🔥 Map q1..q9 to backend proper names
    const getFieldSuffix = (num) => {
        const map = {
            1: "little_interest",
            2: "feeling_down",
            3: "sleep_issues",
            4: "low_energy",
            5: "appetite_issues",
            6: "feeling_bad",
            7: "concentration",
            8: "movement_changes",
            9: "self_harm"
        };
        return map[num];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = buildPayload();

        try {
            const res = await dispatch(createPhqAssessment(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: `PHQ-9 assessment saved successfully.`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/phase04/phq/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire("Error", getApiErrorMessage(err, "Failed to save PHQ-9"), "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <PHQForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary sticky-form-submit">
                                        <i className="ri-save-2-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddPHQ;
